import {Fragment, ReactElement, useEffect, useRef} from "react"
import {observer} from "mobx-react-lite"
import {Avatar, Button, ConfigProvider, List as AntList} from "antd"
import classNames from "classnames"
import {t} from "@ui/common/intl"
import {TShortPoint} from "@ui/api/poi"
import {useStore} from "@ui/stores"
import Loader from "@ui/components/common/Loader"
import EmptySearchResult from "@ui/components/services/common/EmptySearchResult"
import BookmarkIcon from "@ui/icons/track/Bookmark"
import BookmarkOffIcon from "@ui/icons/track/BookmarkOff"
import ErrorSearch from "@ui/icons/track/ErrorSearch"
import {PoiPage} from "../"

type TListProps = {
  loading: boolean
  error: boolean
  onSearch: () => void
  onNext: () => void
  goto: (page: PoiPage, param?: any) => void
}
const List = ({loading, error, onSearch, onNext, goto}: TListProps): ReactElement => {
  const {poi} = useStore()
  const listRef = useRef<HTMLDivElement>(null)

  const handleScroll = () => {
    const list = listRef.current?.firstElementChild
    if (list && list.scrollTop) poi.changeState({list: {scrollTop: list.scrollTop}})
  }

  useEffect(() => {
    const list = listRef.current?.firstElementChild
    const scrollTop = poi.state.list.scrollTop
    if (list && scrollTop && list.scrollHeight - list.clientHeight >= scrollTop) {
      setTimeout(() => list.scroll({top: scrollTop}), 500)
    }
    poi.changeState({list: {scrollTop: undefined}})
  }, [])

  useEffect(() => {
    const list = listRef.current?.firstElementChild
    if (list && list.classList.contains("ant-list")) {
      list.addEventListener("scroll", handleScroll)
      return () => list.removeEventListener("scroll", handleScroll)
    }
  }, [loading, poi.points])

  const handleShow = (point: TShortPoint) => {
    goto(PoiPage.View, point)
  }

  return (
    <div className="list" ref={listRef}>
      {loading && <Loader />}
      {error
        ? (<div className="search-error">
            <div className="error-text">
              <ErrorSearch/>
              <span className="primary-text">{t("poi.list.search.error")}</span>
            </div>
            <div className="reload-button-container">
              <Button className="button-primary" onClick={onSearch}>
                {t("poi.list.search.again")}
              </Button>
            </div>
          </div>)
        : (<Fragment>
            <ConfigProvider renderEmpty={() => <EmptySearchResult/>}>
              <AntList
                dataSource={poi.points || []}
                loadMore={poi.state.list.more && (
                  <Button block onClick={onNext}>
                    {loading ? <Loader /> : <>...</>}
                  </Button>
                )}
                renderItem={(item) => (
                  <AntList.Item
                    key={item.id}
                    onClick={() => handleShow(item)}
                    actions={[
                      item.favorite ? <BookmarkIcon/> : <BookmarkOffIcon/>
                    ]}
                  >
                    <AntList.Item.Meta
                      avatar={
                        <Avatar
                          shape="circle"
                          src={`/poi/${item.type}.svg`}
                          className={classNames("points-list-color", `color-${item.color || 0}`)}
                        />
                      }
                      title={<span className="primary-text">{item.title}</span>}
                    />
                  </AntList.Item>
                )}
              />
            </ConfigProvider>
          </Fragment>)
      }
    </div>
  )
}

export default observer(List)
